import type {
	Appointment,
	AppointmentSoap,
} from "@/types/admin-portal/appointment";

/**
 * The notes every visit carries.
 *
 * The `notes` column is left out on purpose: nothing writes it — the therapist app and
 * the public link both file their remarks under `additionalNotes` — so offering it here
 * would only invite two places to put the same thing.
 */
export const SOAP_CORE_FIELDS = [
	"subject",
	"objective",
	"assessment",
	"planning",
	"additionalNotes",
] as const;

/**
 * Filled in when the therapist closes out a course of therapy, in the order the form
 * asks for them. `followUpTherapyPlan` is a column this form does not edit — nothing
 * clears it, it simply is not asked for here.
 */
export const SOAP_FINAL_FIELDS = [
	"initialPhysicalCondition",
	"therapyGoalEvaluation",
	"nextPhysiotherapyGoals",
	"therapyOutcomeSummary",
] as const;

export type SoapTextField =
	| (typeof SOAP_CORE_FIELDS)[number]
	| (typeof SOAP_FINAL_FIELDS)[number];

/** Upstream refuses anything longer, field by field. */
export const SOAP_TEXT_LIMIT = 10_000;

export type SoapFormValues = Record<SoapTextField, string> & {
	isFinalVisit: boolean;
	needsFollowUp: boolean;
	recommendedVisits: string;
};

const TEXT_FIELDS: SoapTextField[] = [
	...SOAP_CORE_FIELDS,
	...SOAP_FINAL_FIELDS,
];

const snakeCase = (field: string) =>
	field.replace(/[A-Z]/g, (char) => `_${char.toLowerCase()}`);

export const soapFormValues = (soap: AppointmentSoap): SoapFormValues => ({
	...(Object.fromEntries(
		TEXT_FIELDS.map((field) => [field, soap[field] ?? ""]),
	) as Record<SoapTextField, string>),
	isFinalVisit: !!soap.isFinalVisit,
	needsFollowUp: !!soap.needsFollowUp,
	recommendedVisits:
		soap.recommendedVisits == null ? "" : String(soap.recommendedVisits),
});

/**
 * The request body, in the wire's own spelling.
 *
 * Upstream builds its update from the keys the body carries — every field is a
 * pointer there, so one that is missing means "leave this alone". Only what the
 * admin actually changed is sent: a field they cleared travels as "", and one they
 * never touched is left out, which also keeps the audit row down to the real diff.
 */
export const buildSoapPayload = (
	soap: AppointmentSoap,
	values: SoapFormValues,
): Record<string, string | boolean | number> => {
	const payload: Record<string, string | boolean | number> = {};

	for (const field of TEXT_FIELDS) {
		if (values[field] !== (soap[field] ?? "")) {
			payload[snakeCase(field)] = values[field];
		}
	}
	if (values.isFinalVisit !== !!soap.isFinalVisit) {
		payload.is_final_visit = values.isFinalVisit;
	}
	if (values.needsFollowUp !== !!soap.needsFollowUp) {
		payload.needs_follow_up = values.needsFollowUp;
	}
	// An int upstream: a blank box cannot clear the column, it only reads as "leave it",
	// so an emptied count is left out rather than sent as a zero nobody asked for.
	const visits = Number(values.recommendedVisits);
	if (
		values.recommendedVisits !== "" &&
		Number.isFinite(visits) &&
		visits !== soap.recommendedVisits
	) {
		payload.recommended_visits = visits;
	}

	return payload;
};

/**
 * Whether this visit gets the therapy evaluation block at all.
 *
 * It closes out a course of therapy, so it belongs to the last visit of the package.
 * A mid-package visit that already carries one keeps it: an evaluation filed against
 * the wrong visit is exactly what an admin comes here to undo, and a hidden field
 * cannot be cleared. A stored `needsFollowUp: false` is an answer given rather than an
 * evaluation filled in, so it does not count.
 */
export const showsTherapyEvaluation = (
	appointment: Pick<Appointment, "visitNumber" | "totalPackageVisits">,
	soap: AppointmentSoap,
) =>
	(appointment.visitNumber != null &&
		appointment.visitNumber === appointment.totalPackageVisits) ||
	!!soap.isFinalVisit ||
	SOAP_FINAL_FIELDS.some((field) => !!soap[field]) ||
	// Not on the form, but an evaluation all the same: a visit carrying one still opens
	// the section so the rest of it can be corrected.
	!!soap.followUpTherapyPlan ||
	!!soap.needsFollowUp ||
	(soap.recommendedVisits ?? 0) > 0;

export type SoapVisitOption = { name: string | null; numberOfVisit: number };

/**
 * The visit counts the dialog offers, with whatever is already stored kept among them.
 *
 * A stored count need not match any package: it may predate the package list, or its
 * package may since have been retired or made private. Dropping it would leave the
 * control blank while a value sits behind it, so the odd count is offered on its own,
 * in its place in the order, with no package name to give it.
 */
export const visitCountOptions = (
	packages: { name: string; numberOfVisit: number }[],
	stored: string,
): SoapVisitOption[] => {
	const count = Number(stored);
	if (
		stored === "" ||
		!Number.isFinite(count) ||
		packages.some((entry) => entry.numberOfVisit === count)
	) {
		return packages;
	}

	return [...packages, { name: null, numberOfVisit: count }].sort(
		(a, b) => a.numberOfVisit - b.numberOfVisit,
	);
};
